import React, { useState, useEffect } from 'react';
import { X, Calendar, Tag, User, Plus } from 'lucide-react';
import MentionInput from './MentionInput';

const TAG_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700',
  facebook: 'bg-blue-100 text-blue-700',
  fb: 'bg-blue-100 text-blue-700',
  instagram: 'bg-pink-100 text-pink-700',
  ig: 'bg-pink-100 text-pink-700',
  google: 'bg-red-100 text-red-700',
  tiktok: 'bg-gray-800 text-white',
};

const getTagColor = (tag) => TAG_COLORS[tag.toLowerCase()] || 'bg-gray-100 text-gray-700';

const makeInitial = (columnId) => ({
  title: '',
  description: '',
  tags: [],
  due_date: '',
  assigned_to: '',
  priority: 3,
  column_id: columnId || null,
});

const NewTaskModal = ({ isOpen, onClose, onSave, columnId, brandUsers = [] }) => {
  const [task, setTask] = useState(makeInitial(columnId));
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTask(makeInitial(columnId));
      setNewTag('');
    }
  }, [isOpen, columnId]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTask((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    setTask((prev) => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setTask((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tagToRemove) }));
  };

  const handleSave = () => {
    if (!task.title.trim()) {
      alert('Please enter a task title');
      return;
    }
    onSave({
      title: task.title.trim(),
      description: task.description.trim(),
      priority: parseInt(task.priority, 10),
      assigned_to: task.assigned_to.trim() || null,
      due_date: task.due_date || null,
      column_id: task.column_id,
      tags: task.tags,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">Create New Task</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <MentionInput
              as="input"
              type="text"
              value={task.title}
              onChange={(v) => setTask((p) => ({ ...p, title: v }))}
              brandUsers={brandUsers}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Task title — type @ to mention someone"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <MentionInput
              as="textarea"
              value={task.description}
              onChange={(v) => setTask((p) => ({ ...p, description: v }))}
              brandUsers={brandUsers}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Task description — type @ to mention someone"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <Tag size={16} className="mr-1" />
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {task.tags.map((tag) => (
                <div
                  key={tag}
                  className={`${getTagColor(tag)} px-3 py-1.5 rounded-full text-sm flex items-center gap-2 border`}
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:bg-white hover:bg-opacity-20 rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add a tag..."
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Calendar size={16} className="mr-1" />
                Due Date
              </label>
              <input
                type="date"
                name="due_date"
                value={task.due_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <User size={16} className="mr-1" />
                Assignee
              </label>
              <select
                name="assigned_to"
                value={task.assigned_to}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">— Unassigned —</option>
                {brandUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} {u.role === 'OWNER' ? '(Owner)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              name="priority"
              value={task.priority}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>High</option>
              <option value={2}>Medium</option>
              <option value={3}>Low</option>
            </select>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus size={18} className="mr-1" />
            Create Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewTaskModal;
